import { useState, useRef, useEffect } from "react";
import { Redirect, useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { stripWhatsApp } from "@/lib/utils";
import { fireLeadEvent } from "@/hooks/use-tracking";
import { Button } from "@/components/ui/button";
import { WhatsAppInput } from "@/components/ui/whatsapp-input";
import { ArrowRight, Loader2, ShoppingBag, ArrowLeft, MessageCircle } from "lucide-react";
import { CODIGO_CHEGA_EM_30S } from "@/lib/avisoDoCodigo";
import { BrandTile } from "@/components/BrandTile";
import { requestCodeFlow, CodeLoginCard, getApiErrorMessage } from "@/components/CodeLoginCard";

type View = "welcome" | "login";

interface CodeRequest {
  requestId: string;
  whatsappFormattedNumber: string;
  whatsappNumber: string;
  ddi: string;
}

export default function Login() {
  const [view, setView] = useState<View>("welcome");
  const [whatsapp, setWhatsapp] = useState("");
  const [ddi, setDdi] = useState("55");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notFoundFormatted, setNotFoundFormatted] = useState<string | null>(null);
  const [codeRequest, setCodeRequest] = useState<CodeRequest | null>(null);
  const { user, isLoading } = useAuth();
  const [, setLocation] = useLocation();
  const whatsappRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (error) {
      const input = whatsappRef.current?.querySelector("input");
      input?.focus();
    }
  }, [error]);

  if (isLoading) {
    return <div className="h-[100svh] w-full flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  if (user) {
    return <Redirect to="/" />;
  }

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setNotFoundFormatted(null);
    const stripped = stripWhatsApp(whatsapp);
    if (!stripped) {
      setError("Informe o número de WhatsApp");
      return;
    }

    setLoading(true);
    try {
      const result = await requestCodeFlow({ whatsappNumber: stripped, ddi });
      if (result.status === "sent") {
        setCodeRequest({
          requestId: result.requestId,
          whatsappFormattedNumber: result.whatsappFormattedNumber,
          whatsappNumber: stripped,
          ddi,
        });
      } else {
        setNotFoundFormatted(result.whatsappFormattedNumber);
      }
    } catch (err) {
      setError(getApiErrorMessage(err, "Não foi possível enviar o código. Tente novamente."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100svh] w-full flex bg-background">
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 sm:px-16 lg:px-24 xl:px-32">
        <div className="max-w-[400px] w-full mx-auto animate-in slide-in-from-left duration-500 fade-in">
          {view === "welcome" && (
            <div className="space-y-8">
              <div className="text-center space-y-4">
                <BrandTile size="lg" className="mx-auto shadow-lg shadow-primary/25" />
                <div>
                  <h1 className="text-4xl font-bold font-display">Bem-vindo ao<br />Venda no Zap!</h1>
                  <p className="text-muted-foreground mt-2 text-[17px]">Receba seus Pedidos<br /><span className="text-[15px] underline decoration-1 underline-offset-2">Direto pelo WhatsApp</span></p>
                </div>
              </div>

              <div className="space-y-4">
                <button
                  onClick={() => { fireLeadEvent(); setLocation("/register"); }}
                  className="w-full px-6 py-5 rounded-2xl border-2 border-primary bg-primary/5 hover:bg-primary/10 transition-all group cursor-pointer"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <ShoppingBag className="w-6 h-6 text-primary shrink-0" strokeWidth={1.5} />
                      <div className="text-left">
                        <p className="font-bold text-foreground text-[16px] leading-tight whitespace-nowrap">Cadastrar Meu Negócio</p>
                        <p className="text-muted-foreground text-[14px] mt-0.5">Crie sua loja <span className="underline">grátis</span> e comece a vender <span className="underline">agora</span></p>
                      </div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-primary group-hover:translate-x-1 transition-transform shrink-0 ml-2" />
                  </div>
                </button>
              </div>

              <p className="text-center text-muted-foreground text-sm mt-8">
                <button
                  onClick={() => setView("login")}
                  className="text-primary font-semibold hover:underline cursor-pointer"
                >
                  Já tenho uma conta
                </button>
              </p>
            </div>
          )}

          {view === "login" && codeRequest && (
            <CodeLoginCard
              ddi={codeRequest.ddi}
              whatsappNumber={codeRequest.whatsappNumber}
              whatsappFormattedNumber={codeRequest.whatsappFormattedNumber}
              requestId={codeRequest.requestId}
              onBack={() => { setCodeRequest(null); setError(""); }}
            />
          )}

          {view === "login" && !codeRequest && (
            <div className="space-y-6">
              <button
                onClick={() => { setView("welcome"); setError(""); }}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
              >
                <ArrowLeft className="w-4 h-4" /> Voltar
              </button>

              <div className="flex items-center gap-3">
                <BrandTile size="md" />
                <h1 className="text-2xl font-bold font-display">Venda no Zap</h1>
              </div>

              <div>
                <h2 className="text-3xl font-bold tracking-tight text-foreground">Entrar na sua loja</h2>
                <p className="text-muted-foreground mt-2">Informe seu WhatsApp para receber o código de acesso.</p>
              </div>

              <form onSubmit={handleRequestCode} className="space-y-5">
                <div className="space-y-2">
                  <div ref={whatsappRef}>
                    <WhatsAppInput
                      id="whatsapp"
                      value={whatsapp}
                      onChange={(v: string) => {
                        setWhatsapp(v);
                        if (error) setError("");
                        if (notFoundFormatted) setNotFoundFormatted(null);
                      }}
                      ddi={ddi}
                      onDdiChange={setDdi}
                      className={`h-12 text-lg ${error ? "border-red-500 ring-1 ring-red-500" : ""}`}
                      required
                    />
                  </div>
                  {error && (
                    <p className="text-sm text-red-500 font-medium animate-in fade-in slide-in-from-top-1">{error}</p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 text-lg font-semibold group"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Enviando…
                    </>
                  ) : (
                    <>
                      <MessageCircle className="w-5 h-5 mr-2" /> Receber código de acesso
                    </>
                  )}
                </Button>
                {loading && (
                  <p className="text-sm text-muted-foreground text-center" data-testid="aviso-codigo-30s">{CODIGO_CHEGA_EM_30S}</p>
                )}
              </form>

              {notFoundFormatted && (
                <div className="rounded-2xl border-2 border-orange-200 bg-orange-50 dark:bg-orange-950/20 dark:border-orange-800 p-5 space-y-3 animate-in fade-in slide-in-from-top-2">
                  <div>
                    <p className="text-sm font-semibold text-orange-900 dark:text-orange-100">
                      Não encontramos uma conta com este WhatsApp:
                    </p>
                    <p className="font-mono text-base font-semibold text-orange-900 dark:text-orange-100 mt-1">
                      {notFoundFormatted}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => { fireLeadEvent(); setLocation("/register"); }}
                    className="w-full h-11 rounded-xl bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 cursor-pointer"
                  >
                    <ShoppingBag className="w-5 h-5" /> Cadastrar Meu Negócio
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="hidden lg:block lg:w-1/2 relative bg-muted overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-accent/20 mix-blend-multiply z-10" />
        <img
          src={`${import.meta.env.BASE_URL}images/login-hero.png`}
          alt="E-commerce abstract illustration"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent z-10" />

        <div className="absolute bottom-16 left-16 right-16 z-20 text-foreground">
          <h3 className="text-4xl font-display font-bold leading-tight max-w-xl">
            A loja digital perfeita para o seu negócio no WhatsApp.
          </h3>
          <p className="mt-4 text-lg text-muted-foreground max-w-md">
            Receba pedidos organizados, fidelize clientes e aumente suas vendas sem pagar taxas abusivas.
          </p>
        </div>
      </div>
    </div>
  );
}
